import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, KeyRound, ShieldCheck, LayoutDashboard, Ticket, MessageSquare, Smartphone, Landmark, MonitorSmartphone, Mail } from "lucide-react";
import { AppyPaySettings } from "../components/settings/AppyPaySettings";
import { OverviewTab } from "../components/backoffice/OverviewTab";
import { KeysTab } from "../components/backoffice/KeysTab";
import { MessagesTab } from "../components/backoffice/MessagesTab";
import { FiscalTab } from "../components/backoffice/FiscalTab";
import { EmailTab } from "../components/backoffice/EmailTab";
import { BackofficeSessionsDialog } from "../components/backoffice/BackofficeSessionsDialog";

const TABS = [
  { id: "overview", label: "Visão Geral", icon: LayoutDashboard },
  { id: "keys", label: "Chaves de Ativação", icon: Ticket },
  { id: "messages", label: "Mensagens", icon: MessageSquare },
  { id: "appypay", label: "AppyPay", icon: Smartphone },
  { id: "fiscal", label: "Fiscal AGT", icon: Landmark },
  { id: "email", label: "Email", icon: Mail },
];

export default function BackofficePage() {
  const navigate = useNavigate();
  // Sessão em cookie HttpOnly (bo_session) — os pedidos vão com withCredentials + X-Requested-With (defaults globais do axios)
  const headers = useMemo(() => ({}), []);
  const [admin, setAdmin] = useState(null);
  const [tab, setTab] = useState("overview");
  const [prefillCompany, setPrefillCompany] = useState(null);
  const [pwdModal, setPwdModal] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: "", next: "" });
  const [pwdSaving, setPwdSaving] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/backoffice/me`, { withCredentials: true });
      setAdmin(res.data);
    } catch {
      navigate("/backoffice/login");
    }
  }, [navigate]);

  useEffect(() => {
    localStorage.removeItem("backoffice_token");
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await axios.post(`${API_URL}/backoffice/logout`, {}, { withCredentials: true }); } catch { /* sessão já terminada */ }
    navigate("/backoffice/login");
  };

  const changePassword = async () => {
    if (pwdForm.next.length < 8) { toast.error("A nova palavra-passe deve ter pelo menos 8 caracteres"); return; }
    setPwdSaving(true);
    try {
      await axios.post(`${API_URL}/backoffice/change-password`, { current_password: pwdForm.current, new_password: pwdForm.next }, { withCredentials: true });
      toast.success("Palavra-passe alterada com sucesso");
      setPwdModal(false);
      setPwdForm({ current: "", next: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao alterar palavra-passe"); }
    finally { setPwdSaving(false); }
  };

  const goToKeys = (company) => {
    setPrefillCompany(company);
    setTab("keys");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="backoffice-page">
      <header className="sticky top-0 z-20 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <p className="font-bold font-['Outfit'] leading-tight">LUMORA Backoffice</p>
              <p className="text-[11px] text-white/40 leading-tight">{admin?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-white/10" onClick={() => setSessionsOpen(true)} data-testid="backoffice-sessions-btn">
              <MonitorSmartphone className="w-4 h-4 mr-1.5" /> <span className="hidden sm:inline">Sessões</span>
            </Button>
            <Button variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-white/10" onClick={() => setPwdModal(true)} data-testid="backoffice-change-password-btn">
              <KeyRound className="w-4 h-4 mr-1.5" /> <span className="hidden sm:inline">Palavra-passe</span>
            </Button>
            <Button variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-white/10" onClick={logout} data-testid="backoffice-logout-btn">
              <LogOut className="w-4 h-4 mr-1.5" /> <span className="hidden sm:inline">Sair</span>
            </Button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 whitespace-nowrap shrink-0 transition-colors ${tab === t.id ? "border-cyan-400 text-cyan-400" : "border-transparent text-white/50 hover:text-white/80"}`}
              data-testid={`backoffice-tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {admin && tab === "overview" && <OverviewTab headers={headers} onGenerateKey={goToKeys} />}
        {admin && tab === "keys" && <KeysTab headers={headers} prefillCompany={prefillCompany} />}
        {admin && tab === "messages" && <MessagesTab headers={headers} />}
        {admin && tab === "fiscal" && <FiscalTab />}
        {admin && tab === "email" && <EmailTab />}
        {admin && tab === "appypay" && (
          <AppyPaySettings
            headers={headers}
            basePath="/backoffice/appypay/config"
            title="AppyPay da LUMORA — pagamento dos planos"
            description="Conta AppyPay da plataforma usada pelas empresas para pagar os planos Bronze/Ouro por Multicaixa Express, com ativação automática da subscrição."
            showTest={false}
            dark
            webhookTarget="platform"
          />
        )}
      </main>

      <BackofficeSessionsDialog open={sessionsOpen} onOpenChange={setSessionsOpen} />

      <Dialog open={pwdModal} onOpenChange={setPwdModal}>
        <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Alterar Palavra-passe</DialogTitle>
            <DialogDescription className="text-white/50">Altere a palavra-passe da sua conta do backoffice.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-white/70">Palavra-passe atual</Label>
              <Input type="password" value={pwdForm.current} onChange={(e) => setPwdForm({ ...pwdForm, current: e.target.value })} className="bg-white/5 border-white/15 text-white" data-testid="backoffice-current-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/70">Nova palavra-passe (mín. 8 caracteres)</Label>
              <Input type="password" value={pwdForm.next} onChange={(e) => setPwdForm({ ...pwdForm, next: e.target.value })} className="bg-white/5 border-white/15 text-white" data-testid="backoffice-new-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-white/15 bg-transparent text-white/70 hover:bg-white/10 hover:text-white" onClick={() => setPwdModal(false)}>Cancelar</Button>
            <Button className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold" disabled={pwdSaving || !pwdForm.current || !pwdForm.next} onClick={changePassword} data-testid="backoffice-save-password-btn">
              {pwdSaving ? "A guardar..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
